/**
 * Documents domain — cache invalidation registry.
 *
 * The compiler enforces that every DocumentsMutationId has a registry entry.
 * Brand-affecting mutations invalidate the brands domain too (cross-domain).
 */

import type { QueryKey } from '@tanstack/react-query'

import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'
import { listBrandsQueryKey } from '../generated/hooks/productsController'
import {
  getDocumentQuotaQueryKey,
  listDocumentFileExtensionsQueryKey,
  listDocumentsQueryKey,
  listDocumentTagsQueryKey,
} from '../generated/hooks/documentsController'

export type DocumentsMutationId =
  | 'updateDocument'
  | 'deleteDocument'
  | 'bulkAssignDocumentBrand'
  | 'uploadDocument'
  | 'replaceDocument'
// NOTE (controller trim): tag create/update/delete mutations are NOT wrapped —
// no documentsApi.ts consumer uses tag CRUD (that lives in api.ts / the tag
// settings page, a separate future migration), and PATCH updateDocumentTag is
// pre-existing broken server-side. The generated tag-mutation clients exist but
// stay unused this ticket. listDocumentTags (read) + searchDocumentTags remain.

/** Keys every document mutation should invalidate. */
export function commonDocumentInvalidations(): QueryKey[] {
  return [
    listDocumentsQueryKey(),
    getDocumentQuotaQueryKey(),
    listDocumentFileExtensionsQueryKey(),
    listDocumentTagsQueryKey(),
  ]
}

/** Cross-domain: brand list caches (generated + legacy). */
function brandInvalidations(): QueryKey[] {
  return [listBrandsQueryKey(), ['brands']]
}

/** The documents table registers its own dynamic keys (DOCUMENTS_* / documents-*). */
const documentTableRemovals = () => [
  containsKeyFilter('DOCUMENTS_'),
  containsKeyFilter('documents-'),
]

export const invalidationRegistry: Record<
  DocumentsMutationId,
  InvalidationRule<any>
> = {
  updateDocument: {
    invalidates: () => [
      ...commonDocumentInvalidations(),
      ...brandInvalidations(),
    ],
    removes: documentTableRemovals,
  },
  deleteDocument: {
    invalidates: () => [
      ...commonDocumentInvalidations(),
      ...brandInvalidations(),
    ],
    removes: documentTableRemovals,
  },
  bulkAssignDocumentBrand: {
    invalidates: () => [
      ...commonDocumentInvalidations(),
      ...brandInvalidations(),
    ],
    removes: documentTableRemovals,
  },
  uploadDocument: {
    invalidates: () => [
      ...commonDocumentInvalidations(),
      ...brandInvalidations(),
    ],
    removes: documentTableRemovals,
  },
  replaceDocument: {
    invalidates: () => [
      ...commonDocumentInvalidations(),
      ...brandInvalidations(),
    ],
    removes: documentTableRemovals,
  },
}
